import api from './api';

// 10 типових з enums/collaboration-step-type.enum.ts (api) + "custom". Підпис рядка плану (GET
// .../collaboration-plan) - clientProfile.collaborationPlan.step.<type> (i18n), бек там не повертає.
// Каталог для select'а (GET /collaboration-plan/options, getOptions нижче) - підпис уже з бекенду.
export type CollaborationStepType =
  | 'personal_questionnaire'
  | 'schedule_first_consultation'
  | 'first_meeting'
  | 'meeting_summary'
  | 'create_client_plan'
  | 'schedule_repeat_meeting'
  | 'repeat_meeting'
  | 'progress_review_send'
  | 'progress_review_view'
  | 'progress_review_remind'
  | 'custom';

// Намір дії "перейти до виконання" (кнопка-стрілка) - бек описує НАМІР (куди це веде за змістом),
// фронт сам вирішує ЯК (навігація) - collaborationStepActions.ts. `params` - довільні
// додаткові поля інтенту (напр. `dateView` для `calendar`), фронт підставляє як query "в лоб".
export interface CollaborationStepActionIntent {
  type: 'questionnaires' | 'appointments' | 'calendar' | 'chat';
  params?: Record<string, string>;
}

export interface CollaborationStep {
  id: number;
  clientId: number | null;
  expertId: number;
  type: CollaborationStepType;
  status: 'pending' | 'done';
  title: string | null;
  // T2 - локальна дата+час, без Z (напр. "2026-10-29T11:00:00"), null якщо не заплановано.
  scheduledLdt: string | null;
  // Синхронізована task-подія в календарі експерта - інформаційне, UI не використовує напряму.
  calendarEventId: number | null;
  // null - немає дії (та сама умова, що раніше `hasAction=false` у collaborationStepIcons.tsx) -
  // видимість стрілки в CollaborationPlanRow тепер випливає з цього поля, не з окремого списку
  // типів на фронті.
  actionIntent: CollaborationStepActionIntent | null;
  createdAt: string;
  completedAt: string | null;
}

export interface CreateCollaborationStepInput {
  // Тип з каталогу; відсутній (або "custom") -> довільна задача, тоді title обов'язковий.
  type?: CollaborationStepType;
  title?: string;
  scheduledLdt?: string;
}

// Каталог для select'а при додаванні задачі - типові (без "custom", довільна задача - окремий шлях
// без type). typeCode збігається з CollaborationStepType, крім "custom".
export interface CollaborationStepOption {
  typeCode: Exclude<CollaborationStepType, 'custom'>;
  label: string;
}

export const collaborationPlanService = {
  // Швидко завантажується й кешується на весь сеанс сторінки - каталог однаковий для будь-якого
  // клієнта, не залежить від clientId (бек: GET /collaboration-plan/options, без clientId у шляху).
  getOptions: async (): Promise<CollaborationStepOption[]> => {
    const res = await api.get('/collaboration-plan/options');
    return res.data.data as CollaborationStepOption[];
  },

  // `total` - реальна кількість активних задач (без DASHBOARD_LIMIT картки, бек: `meta.total`) -
  // для футера "Переглянути всі задачі (N)", інакше при >4 активних задачах N буде заниженим
  // (рахувалось з `items.length` капованого списку).
  getPlan: async (clientId: number | string): Promise<{ items: CollaborationStep[]; total: number }> => {
    const res = await api.get(`/client/${clientId}/collaboration-plan`);
    const items = res.data.data as CollaborationStep[];
    return { items, total: res.data.meta?.total ?? items.length };
  },

  // Для бокової панелі "Переглянути всі задачі" - без лімітy getPlan (4), з перемикачем
  // активні/виконані.
  getAllSteps: async (clientId: number | string, status: 'pending' | 'done'): Promise<CollaborationStep[]> => {
    const res = await api.get(`/client/${clientId}/collaboration-plan/all`, { params: { status } });
    return res.data.data as CollaborationStep[];
  },

  createStep: async (clientId: number | string, input: CreateCollaborationStepInput) => {
    const res = await api.post(`/client/${clientId}/collaboration-plan`, input);
    return res.data.data as CollaborationStep;
  },

  // Один запит - один атомарний запис (бек: createStepsBatch, одна транзакція) - не N окремих
  // createStep. Напр. "Рекомендації" (CollaborationPlanPresetDialog.tsx).
  createStepsBatch: async (clientId: number | string, items: CreateCollaborationStepInput[]) => {
    const res = await api.post(`/client/${clientId}/collaboration-plan/batch`, { items });
    return res.data.data as CollaborationStep[];
  },

  // Та сама форма, що й createStep - повна заміна type/title/scheduledLdt, не частковий patch.
  updateStep: async (clientId: number | string, stepId: number, input: CreateCollaborationStepInput) => {
    const res = await api.patch(`/client/${clientId}/collaboration-plan/${stepId}`, input);
    return res.data.data as CollaborationStep;
  },

  markDone: async (clientId: number | string, stepId: number) => {
    const res = await api.patch(`/client/${clientId}/collaboration-plan/${stepId}/complete`);
    return res.data.data as CollaborationStep;
  },

  remove: async (clientId: number | string, stepId: number) => {
    await api.delete(`/client/${clientId}/collaboration-plan/${stepId}`);
  },
};
